import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import CustomButton from "./Button";
import { Tag } from "./DashTable";
import { useToast } from "./Toast";
import apiService from "../Services/api.service";
import { UI, btnSx } from "./ActionDrawer";

const Btn = ({ variant = "solid", sx, ...props }) => (
  <CustomButton {...props} sx={{ ...btnSx(variant, props.disabled), ...sx }} />
);

const rupee = (paise) =>
  paise === null || paise === undefined
    ? "—"
    : `₹${(Number(paise) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const CATEGORY_LABEL = {
  travel: "Travel",
  fuel: "Fuel",
  accommodation: "Accommodation",
  food: "Food allowance",
  parts: "Parts / consumables",
  courier: "Courier",
  other: "Other",
};

const STATUS_TAG = {
  submitted: { tone: "s", label: "Submitted" },
  queried: { tone: "b", label: "Queried" },
  approved: { tone: "g", label: "Approved" },
  rejected: { tone: "b", label: "Rejected" },
  paid: { tone: "g", label: "Paid" },
};

const dayKey = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "unknown" : d.toISOString().slice(0, 10);
};

// Same grouping the filing form uses — a day can carry several categories,
// so review should read the same way, not as a flat item-per-line list.
const groupItemsByDay = (items) => {
  const map = new Map();
  items.forEach((item) => {
    const key = dayKey(item.date);
    if (!map.has(key)) map.set(key, { date: item.date, items: [] });
    map.get(key).items.push(item);
  });
  return Array.from(map.values()).sort((a, b) => new Date(a.date) - new Date(b.date));
};

const DECISION_OPTIONS = [
  { value: "approve", label: "Approve" },
  { value: "reject", label: "Reject" },
  { value: "query", label: "Query" },
];

/* claim comes from the approval queue: claimantName, clinicName, orderId,
   items[] (category, amountPaise, date, description, policyFlag),
   totalAmountPaise, dateFrom, dateTo, claimantHistory. No receipt for now. */
const ReviewExpenseClaimModal = ({ open, onClose, onSaved, claim, onViewOrder }) => {
  const toast = useToast();
  const token = localStorage.getItem("token");
  const isMobile = useMediaQuery("(max-width:600px)");

  const [decision, setDecision] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // This dialog instance is reused across different claims — without this,
  // a decision picked for one claim stayed visually "selected" on the next.
  useEffect(() => {
    if (open) {
      setDecision("");
      setNote("");
    }
  }, [open, claim?.id]);

  if (!claim) return null;

  const handleSubmit = async () => {
    if (!decision) {
      toast.error("Choose a decision");
      return;
    }
    if (decision !== "approve" && !note.trim()) {
      toast.error("A reason or note is required");
      return;
    }
    setSubmitting(true);
    try {
      if (decision === "approve") await apiService.approveExpenseClaim(claim.id, token);
      else if (decision === "reject") await apiService.rejectExpenseClaim(claim.id, note, token);
      else await apiService.queryExpenseClaim(claim.id, note, token);
      toast.success("Decision recorded");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to record decision");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth={!isMobile}
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: UI.surface,
          backgroundImage: "none",
          border: isMobile ? "none" : "1px solid",
          borderColor: UI.line,
          borderRadius: isMobile ? 0 : "10px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "10px",
          padding: { xs: "14px 16px", sm: "16px 18px" },
          borderBottom: "1px solid",
          borderColor: UI.line,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <Box sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 600, color: UI.ink }}>Review expense claim</Box>
            {STATUS_TAG[claim.status] && (
              <Tag tone={STATUS_TAG[claim.status].tone}>{STATUS_TAG[claim.status].label}</Tag>
            )}
          </Box>
          <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px" }}>
            {[claim.claimantName, claim.clinicName].filter(Boolean).join(" · ")}
          </Box>
        </Box>
        <IconButton onClick={onClose} size={isMobile ? "medium" : "small"}>
          <CloseIcon sx={{ fontSize: isMobile ? 22 : 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ padding: { xs: "14px", sm: "18px" }, paddingBottom: isMobile ? "104px" : "18px", bgcolor: UI.bg2 }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            marginBottom: "14px",
            padding: { xs: "10px 12px", sm: "12px 14px" },
            bgcolor: UI.surface,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "8px",
          }}
        >
          <Box sx={{ fontSize: 13, color: UI.ink3 }}>
            {claim.itemCount} item{claim.itemCount === 1 ? "" : "s"} ·{" "}
            {shortDate(claim.dateFrom)}
            {claim.dateFrom !== claim.dateTo ? ` – ${shortDate(claim.dateTo)}` : ""}
          </Box>
          <Box sx={{ fontSize: 19, fontWeight: 600, color: UI.ink }}>{rupee(claim.totalAmountPaise)}</Box>
        </Box>

        {claim.orderId && (
          <Box sx={{ marginBottom: "12px" }}>
            <Box
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onViewOrder?.(claim.orderId);
              }}
              sx={{ fontSize: 12, color: UI.p, textDecoration: "none" }}
            >
              View linked order
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "16px" }}>
          {groupItemsByDay(claim.items).map((day) => {
            const dayTotal = day.items.reduce((sum, item) => sum + item.amountPaise, 0);
            return (
              <Box
                key={dayKey(day.date)}
                sx={{ border: "1px solid", borderColor: UI.line, borderRadius: "8px", overflow: "hidden" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 12px",
                    bgcolor: UI.bg2,
                    borderBottom: "1px solid",
                    borderColor: UI.line,
                  }}
                >
                  <Box sx={{ fontSize: 12, fontWeight: 500, color: UI.ink2 }}>{shortDate(day.date)}</Box>
                  <Box sx={{ fontSize: 12, color: UI.ink3 }}>{rupee(dayTotal)}</Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {day.items.map((item, i) => {
                    const flagged = item.policyFlag?.flagged;
                    return (
                      <Box
                        key={item.id}
                        sx={{
                          padding: "10px 12px",
                          bgcolor: UI.surface,
                          borderBottom: i === day.items.length - 1 ? 0 : "1px solid",
                          borderColor: UI.line,
                        }}
                      >
                        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                          <Box sx={{ fontSize: 13, color: UI.ink }}>{CATEGORY_LABEL[item.category] || item.category}</Box>
                          <Box sx={{ fontSize: 13, fontWeight: 600, color: UI.ink }}>{rupee(item.amountPaise)}</Box>
                        </Box>
                        {item.description && (
                          <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "4px" }}>{item.description}</Box>
                        )}
                        {flagged && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", flexWrap: "wrap" }}>
                            <Tag tone="s">Over daily limit</Tag>
                            {item.policyFlag?.reason && (
                              <Box sx={{ fontSize: 11, color: UI.ink3 }}>{item.policyFlag.reason}</Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
          <Tag tone="i">{claim.claimantHistory?.totalClaims ?? 0} claims total</Tag>
          <Tag tone="g">{claim.claimantHistory?.approvedCount ?? 0} approved</Tag>
          {(claim.claimantHistory?.rejectedCount ?? 0) > 0 && (
            <Tag tone="b">{claim.claimantHistory.rejectedCount} rejected</Tag>
          )}
        </Box>

        <Box sx={{ fontSize: 12, color: UI.ink3, marginBottom: "8px", fontWeight: 500 }}>Decision</Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
          {DECISION_OPTIONS.map((opt) => {
            const selected = decision === opt.value;
            return (
              <Box
                key={opt.value}
                component="button"
                type="button"
                onClick={() => setDecision(opt.value)}
                sx={{
                  ...btnSx(selected ? "solid" : "ghost"),
                  flex: "1 1 80px",
                  minHeight: "44px",
                  textAlign: "center",
                  borderRadius: "8px",
                }}
              >
                {opt.label}
              </Box>
            );
          })}
        </Box>

        <TextField
          label="Reason / note — required for reject or query"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ "& .MuiInputBase-input": { fontSize: { xs: 16, sm: 14 } } }}
        />
      </DialogContent>
      <Box
        sx={{
          position: isMobile ? "fixed" : "static",
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "flex-end",
          gap: "10px",
          padding: { xs: "12px 16px", sm: "14px 18px" },
          borderTop: "1px solid",
          borderColor: UI.line,
          bgcolor: UI.surface,
        }}
      >
        <Btn
          text="Cancel"
          variant="ghost"
          onClick={onClose}
          sx={{ borderRadius: "8px", width: { xs: "100%", sm: "auto" }, minHeight: { xs: "44px", sm: "auto" } }}
        />
        <Btn
          text={submitting ? "Submitting…" : "Submit decision"}
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ borderRadius: "8px", width: { xs: "100%", sm: "auto" }, minHeight: { xs: "44px", sm: "auto" } }}
        />
      </Box>
    </Dialog>
  );
};

export default ReviewExpenseClaimModal;
